import React, { useEffect, useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const StatisticsEvolutionTab = ({ startDate, endDate, clientFilter, toolFilter, fetchWithAuth, apiBaseUrl, includeInternal }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [granularity, setGranularity] = useState('DAY');

  const formatYMD = (d) => new Date(d).toISOString().split('T')[0];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          startDate: formatYMD(startDate),
          endDate: formatYMD(endDate)
        });
        if (clientFilter?.value) params.set('client', clientFilter.value);
        if (toolFilter?.value) params.set('tool', toolFilter.value);

        const respUsage = await fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/tools/stats/evolution?${params.toString()}`);
        if (!respUsage.ok) throw new Error('Failed to load evolution data');
        const jsonUsage = await respUsage.json();
        setData(jsonUsage.data || []);
        if (jsonUsage.metadata?.granularity) setGranularity(jsonUsage.metadata.granularity);
      } catch (e) {
        console.error(e);
        setError('Erreur lors du chargement des données d\'évolution');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [startDate, endDate, clientFilter, toolFilter, fetchWithAuth, apiBaseUrl]);

  // Transform data for Recharts: array of objects with date, Clients, Globe
  const chartData = useMemo(() => {
    if (!data.length) return [];

    // Group by period
    const map = new Map();
    data.forEach(row => {
      // Handle potential uppercase keys from DB (Snowflake/etc)
      const period = row.period || row.PERIOD;
      const category = row.category || row.CATEGORY;
      const totalVisits = row.total_visits || row.TOTAL_VISITS;

      if (!period) return;

      // row.period is ISO string from DB usually
      const d = new Date(period);
      if (isNaN(d.getTime())) return; // Safety check

      const key = d.toISOString().split('T')[0];

      if (!map.has(key)) {
        map.set(key, { period: key, originalDate: d, Clients: 0, Globe: 0 });
      }
      const entry = map.get(key);

      const val = Number(totalVisits) || 0;
      if (category === 'Globe') {
        entry.Globe += val;
      } else {
        entry.Clients += val;
      }
    });

    // Sort by date
    return Array.from(map.values()).sort((a, b) => new Date(a.period) - new Date(b.period));
  }, [data]);

  if (loading) {
    return (
      <div className="flex justify-center my-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-500 bg-red-50 rounded-md">{error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold dark:text-white">Évolution des usages (Visites)</h3>
          <div className="text-sm text-gray-500">
            Granularité: {granularity === 'WEEK' ? 'Hebdomadaire' : 'Journalière'}
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className="text-gray-500 text-center py-8">Aucune donnée disponible pour cette période.</div>
        ) : (
          <div className="h-96 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="period"
                  tickFormatter={(val) => {
                    const d = new Date(val);
                    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                  }}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(label) => new Date(label).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  formatter={(value, name) => [value, name]}
                  cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                />
                <Legend />
                <Bar dataKey="Clients" stackId="a" fill="#3b82f6" name="Clients (Hors Globe)" radius={[0, 0, 0, 0]} />
                {includeInternal && (
                  <Bar dataKey="Globe" stackId="a" fill="#10b981" name="Globe" radius={[4, 4, 0, 0]} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatisticsEvolutionTab;

